import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import WhatsAppChat from 'components/ui/WhatsAppChat';
import CallButton from 'components/ui/CallButton';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import InteractiveMap from './components/InteractiveMap';
import FAQSection from './components/FAQSection';

const ContactDirections = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact & Directions - Dandeli Adventure Resorts | Get in Touch</title>
        <meta 
          name="description" 
          content="Contact Dandeli Adventure Resorts for bookings and inquiries. Find directions, phone numbers, email addresses, and interactive maps. Located in Dandeli, Karnataka." 
        />
        <meta 
          name="keywords" 
          content="Dandeli resort contact, Dandeli directions, resort booking contact, Karnataka adventure resort, Dandeli location map" 
        />
        <meta property="og:title" content="Contact & Directions - Dandeli Adventure Resorts" />
        <meta property="og:description" content="Get in touch with Dandeli Adventure Resorts. Find our location, contact information, and directions to reach us." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://dandeliadventure.com/contact-directions" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-20 pb-12 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
                Contact & Directions
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Get in touch with us for bookings, inquiries, or assistance. We're here to help make your Dandeli adventure unforgettable.
              </p>
              
              {/* Quick Contact Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-2">24/7</div>
                  <div className="text-sm text-muted-foreground">Customer Support</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-2">&lt; 2 hrs</div>
                  <div className="text-sm text-muted-foreground">Response Time</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-2">15+ Languages</div>
                  <div className="text-sm text-muted-foreground">Support Available</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Form - Left Column */}
              <div className="lg:col-span-2">
                <ContactForm />
              </div>

              {/* Contact Information - Right Column */}
              <div className="lg:col-span-1">
                <ContactInfo />
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Map Section */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <InteractiveMap />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <FAQSection />
          </div>
        </section>

        {/* Emergency Contact Banner */}
        <section className="py-12 bg-error/5 border-t border-error/20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
                Emergency Contact Information
              </h2>
              <p className="text-muted-foreground mb-6">
                For urgent assistance during your stay or travel to our resort
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-error/10 rounded-lg">
                    <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-foreground">24/7 Emergency</div>
                    <div className="text-sm text-muted-foreground">+91 98765 43212</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-error/10 rounded-lg">
                    <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-foreground">Local Police</div>
                    <div className="text-sm text-muted-foreground">100 (Toll Free)</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-error/10 rounded-lg">
                    <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-foreground">Medical Emergency</div>
                    <div className="text-sm text-muted-foreground">108 (Ambulance)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Logo and Description */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-6 h-6 text-primary-foreground"
                      fill="currentColor"
                    >
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-primary">
                      Dandeli Adventure
                    </h3>
                    <p className="text-xs text-muted-foreground -mt-1">
                      Resorts
                    </p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">
                  Experience the perfect blend of adventure and comfort in the heart of Dandeli's pristine wilderness. Your gateway to unforgettable memories.
                </p>
                <div className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} Dandeli Adventure Resorts. All rights reserved.
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold text-card-foreground mb-4">Quick Links</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="/home-landing" className="text-muted-foreground hover:text-primary transition-micro">Home</a></li>
                  <li><a href="/accommodation-details" className="text-muted-foreground hover:text-primary transition-micro">Accommodations</a></li>
                  <li><a href="/activities-experiences" className="text-muted-foreground hover:text-primary transition-micro">Activities</a></li>
                  <li><a href="/photo-gallery" className="text-muted-foreground hover:text-primary transition-micro">Gallery</a></li>
                  <li><a href="/booking-engine" className="text-muted-foreground hover:text-primary transition-micro">Book Now</a></li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-semibold text-card-foreground mb-4">Contact Info</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>+91 98765 43210</li>
                  <li>info@dandeliadventure.com</li>
                  <li>Kali River Road, Dandeli<br />Karnataka 581325</li>
                </ul>
              </div>
            </div>
          </div>
        </footer>

        {/* Floating Action Buttons */}
        <WhatsAppChat />
        <CallButton />
      </div>
    </>
  );
};

export default ContactDirections;